import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import WaitlistForm from '../components/WaitlistForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiCheck, FiClock, FiShield, FiGlobe, FiFileText, FiSettings, FiArrowRight } = FiIcons;

const HomePage = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "NinjaCopia",
    "applicationCategory": "FiniteApplication",
    "operatingSystem": "Windows 10, Windows 11",
    "offers": {
      "@type": "Offer",
      "price": "59.00",
      "priceCurrency": "USD"
    },
    "description": "Professional Multi-Account Copier for NinjaTrader with <5ms latency.",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "12"
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Trade Once. Execute Everywhere."
        description="NinjaCopia eliminates manual trade copying. Execute on your Master account and automatically replicate trades across all your NinjaTrader accounts with <5ms latency."
        keywords="NinjaTrader, trade copier, multi-account trading, prop trading, automated trading"
        schema={schema}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900/20 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6"
            >
              Trade Once. Execute Everywhere.
            </motion.h1>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-xl sm:text-2xl lg:text-3xl text-gray-600 dark:text-gray-300 mb-8 font-medium"
            >
              The Professional Multi-Account Copier for NinjaTrader
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed"
            >
              NinjaCopia eliminates manual trade copying. Execute on your Master account and automatically replicate trades across all your NinjaTrader accounts with &lt;5ms latency.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-md font-semibold text-blue-600 dark:text-blue-400 mb-10"
            >
              Perfect for: Funded prop traders • Multi-account managers • Trading firms scaling their operation
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
            >
              <button className="inline-flex items-center px-8 py-4 bg-gray-800 dark:bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-900 dark:hover:bg-gray-600 transition-all duration-200">
                <SafeIcon icon={FiPlay} className="w-5 h-5 mr-2" />
                Watch Demo Video
              </button>
              <Link
                to="/pricing"
                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-200"
              >
                Join Waitlist for Early Access
                <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Demo Video Placeholder */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="aspect-video bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center">
            <div className="text-center">
              <SafeIcon icon={FiPlay} className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <p className="text-xl font-medium text-gray-500 dark:text-gray-400">Demo Video Coming Soon</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Placeholder */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl font-medium text-gray-900 dark:text-white mb-4 italic">
            "You can be the first testimonial here - hit me up."
          </blockquote>
          <p className="text-gray-600 dark:text-gray-400">– Carlos M., Prop Trader (Madrid)</p>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Problem */}
            <div>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">The Multi-Account Execution Headache</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4 mt-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Manual errors</h4>
                    <p className="text-gray-600 dark:text-gray-400">when copying trades between accounts</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4 mt-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Wasted time</h4>
                    <p className="text-gray-600 dark:text-gray-400">on repetitive execution tasks</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4 mt-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Inconsistent fills</h4>
                    <p className="text-gray-600 dark:text-gray-400">across different prop firms</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4 mt-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">No centralized risk management</h4>
                    <p className="text-gray-600 dark:text-gray-400">for your entire portfolio</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Solution */}
            <div>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">How NinjaCopia Solves It</h3>
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-4 shrink-0">
                    <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-lg">CONNECT</h4>
                    <p className="text-gray-600 dark:text-gray-400">your NinjaTrader accounts (Rithmic, Continuum, ATAS)</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-4 shrink-0">
                    <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-lg">CONFIGURE</h4>
                    <p className="text-gray-600 dark:text-gray-400">risk rules and lot sizing per account</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-4 shrink-0">
                    <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-lg">TRADE</h4>
                    <p className="text-gray-600 dark:text-gray-400">on your Master—we replicate instantly with &lt;5ms latency</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Built for Serious Traders</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiClock} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">Real-Time Sync</h4>
              <p className="text-gray-600 dark:text-gray-400">&lt;5ms latency, zero missed trades</p>
            </div>
            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">Smart Risk Management</h4>
              <p className="text-gray-600 dark:text-gray-400">Per-account equity & position limits</p>
            </div>
            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiGlobe} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">Multi-Language UI</h4>
              <p className="text-gray-600 dark:text-gray-400">English, Spanish, and French interfaces</p>
            </div>
            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiFileText} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">Detailed Execution Logs</h4>
              <p className="text-gray-600 dark:text-gray-400">Every trade recorded for audit and analysis</p>
            </div>
            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiSettings} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">Simple Setup</h4>
              <p className="text-gray-600 dark:text-gray-400">Configure once, then forget about it</p>
            </div>
            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiCheck} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">Global Support</h4>
              <p className="text-gray-600 dark:text-gray-400">Documentation and support in 3 languages</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-blue-600 dark:bg-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-bold text-white mb-6">Ready to Scale Your Trading Operation?</h3>
          <p className="text-xl text-blue-100 mb-8">Join the exclusive waitlist for:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 text-left">
            <div className="flex items-center text-blue-100">
              <SafeIcon icon={FiCheck} className="w-5 h-5 mr-3 text-blue-200" />
              Early access to NinjaCopia
            </div>
            <div className="flex items-center text-blue-100">
              <SafeIcon icon={FiCheck} className="w-5 h-5 mr-3 text-blue-200" />
              Founder's pricing (50% discount for first 100 customers)
            </div>
            <div className="flex items-center text-blue-100">
              <SafeIcon icon={FiCheck} className="w-5 h-5 mr-3 text-blue-200" />
              Priority onboarding and setup support
            </div>
            <div className="flex items-center text-blue-100">
              <SafeIcon icon={FiCheck} className="w-5 h-5 mr-3 text-blue-200" />
              Updates on NinjaTrader vendor approval progress
            </div>
          </div>
          <WaitlistForm className="mb-6" variant="inverted" />
        </div>
      </section>
    </div>
  );
};

export default HomePage;